import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { PatchAppointmentBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/** Updates editable fields on an appointment, then refreshes the appointments list. */
export function usePatchAppointment(opts?: { consultationId?: string }) {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...body }: PatchAppointmentBody & { id: string }) => {
      const response = await api.patch('/appointments/:id', { params: { id }, body })
      if (!response.success) {
        throw new Error(response.error.message)
      }
      return response.data
    },
    onSuccess: ({ appointment }) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots-range'] })
      queryClient.invalidateQueries({ queryKey: ['patient-appointments'] })
      queryClient.invalidateQueries({ queryKey: ['patient-timeline'] })
      queryClient.invalidateQueries({ queryKey: ['patient-appointment-types'] })
      // A no-show or late change can charge a penalty fee
      queryClient.invalidateQueries({ queryKey: ['patient', appointment.patientId, 'invoices'] })
      // Consultations embed the linked appointment — refresh so derived status updates
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      if (opts?.consultationId) {
        queryClient.invalidateQueries({ queryKey: ['consultation', opts.consultationId] })
      }
    },
  })
}
